import React, { useCallback, useEffect, useState } from "react";
import {
  Panel,
  PanelHeader,
  PanelBody,
} from "../../components/panel/panel.jsx";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import Select from "react-select";
import APIs from "../../Constants/apis.js";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import CountrySelector from "../../components/CustomComponents/DropdownSelections/CountrySelector.jsx";
import CurrencySelector from "../../components/CustomComponents/DropdownSelections/CurrencySelector.jsx";
import swal from "sweetalert";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";

function UpdateAddon() {
  const navigate = useNavigate();
  const { addonID } = useParams();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const [loading, setLoading] = useState(false);

  const addonTypeOptions = [
    { value: "User", label: "User License" },
    { value: "Storage", label: "Storage" },
    { value: "Feature", label: "Feature" },
    { value: "Support", label: "Support" },
    { value: "Location", label: "Location" },
    { value: "Custom", label: "Custom" },
  ];

  const unitOptions = [
    { value: "Location", label: "Location" },
    { value: "Device", label: "Device" },
  ];

  const [selectedType, setSelectedType] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);

  const [addonData, setAddonData] = useState({
    addonID: 0,
    addonName: "",
    addonDescription: "",
    addonType: "",
    addonValue: 1,
    addonUnit: "",
    isQuantifiable: true,
    isActive: true,
    isPublished: false,
    sortOrder: 0,
  });

  // Pricing state
  const [prices, setPrices] = useState([]);
  const [newPrice, setNewPrice] = useState({
    currency: null,
    price: 0,
    actualPrice: 0,
    duration: 30, // Default to monthly
    country: "",
  });

  // Fetch addon and prices
  const fetchAddonData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch addon with prices
      const response = await axiosInstance.get(APIs.GET_ADDON_WITH_PRICES, {
        params: { addonID: addonID },
      });

      if (response.data.status) {
        const addon = response.data.data;

        setAddonData({
          addonID: addon.addonID,
          addonName: addon.addonName || "",
          addonDescription: addon.addonDescription || "",
          addonType: addon.addonType || "",
          addonValue: addon.addonValue || 1,
          addonUnit: addon.addonUnit || "",
          isQuantifiable: addon.isQuantifiable !== undefined ? addon.isQuantifiable : true,
          isActive: addon.isActive !== undefined ? addon.isActive : true,
          isPublished: addon.isPublished !== undefined ? addon.isPublished : false,
          sortOrder: addon.sortOrder || 0,
        });

        // Set selected type
        const typeOption = addonTypeOptions.find(
          (opt) => opt.value === addon.addonType
        );
        setSelectedType(typeOption || null);

        // Set selected unit
        const unitOption = unitOptions.find(
          (opt) => opt.value === addon.addonUnit
        );
        setSelectedUnit(unitOption || null);

        // Set prices
        if (addon.prices && Array.isArray(addon.prices)) {
          const formattedPrices = addon.prices.map((p) => ({
            id: p.addonPriceID,
            addonPriceID: p.addonPriceID,
            currency: {
              value: p.priceCurrency,
              label: p.priceCurrency,
            },
            price: p.price,
            actualPrice: p.actualPrice,
            duration: p.duration,
            country: p.country || "",
          }));
          setPrices(formattedPrices);
        }
      } else {
        showErrorToast("Failed to load addon data");
        navigate("/addons/manageAddons");
      }
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
      showErrorToast("Failed to load addon data");
      navigate("/addons/manageAddons");
    }
  }, [addonID, navigate]);

  useEffect(() => {
    fetchAddonData();
  }, [fetchAddonData]);

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setAddonData({ ...addonData, [field]: value });
  };

  const handlePriceInputChange = (e, field) => {
    setNewPrice({ ...newPrice, [field]: e.target.value });
  };

  const handleAddPrice = () => {
    if (!newPrice.currency) {
      showErrorToast("Please select a currency");
      return;
    }

    if (newPrice.price <= 0) {
      showErrorToast("Price must be greater than 0");
      return;
    }

    // Check if currency already exists
    const exists = prices.find(
      (p) =>
        p.currency.value === newPrice.currency.value &&
        p.country === newPrice.country
    );

    if (exists) {
      showErrorToast(
        `Price for ${newPrice.currency.label} ${newPrice.country ? `(${newPrice.country})` : ""
        } already exists`
      );
      return;
    }

    setPrices([
      ...prices,
      {
        ...newPrice,
        id: Date.now(), // Temporary ID for UI
        addonPriceID: 0, // 0 indicates new price
      },
    ]);

    // Reset form
    setNewPrice({
      currency: null,
      price: 0,
      actualPrice: 0,
      duration: 30,
      country: "",
    });

    showSuccessToast("Price added to list");
  };

  const handleRemovePrice = async (price) => {
    // If price has addonPriceID > 0, it's from database - need to delete from backend
    if (price.addonPriceID > 0) {
      const result = await swal({
        title: "Delete Price",
        text: "Are you sure you want to delete this price?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        try {
          setLoading(true);
          const response = await axiosInstance.delete(APIs.DELETE_ADDON_PRICE, {
            params: { addonPriceID: price.addonPriceID },
          });

          if (response.data.status) {
            setPrices(prices.filter((p) => p.id !== price.id));
            showSuccessToast("Price deleted successfully");
          } else {
            showErrorToast("Failed to delete price");
          }
          setLoading(false);
        } catch (error) {
          console.error(error);
          setLoading(false);
          showErrorToast("Failed to delete price");
        }
      }
    } else {
      // Just remove from UI (not saved yet)
      setPrices(prices.filter((p) => p.id !== price.id));
      showSuccessToast("Price removed from list");
    }
  };

  const handleSubmit = async () => {
    const { addonName, addonType } = addonData;

    if (!addonName || !addonType) {
      showErrorToast("Please fill in addon name and type.");
      return;
    }

    try {
      setLoading(true);

      // Step 1: Update Addon
      const addonPayload = {
        AddonID: addonData.addonID,
        AddonName: addonData.addonName,
        AddonDescription: addonData.addonDescription,
        AddonType: addonData.addonType,
        AddonValue: parseInt(addonData.addonValue) || 1,
        AddonUnit: addonData.addonUnit,
        IsQuantifiable: addonData.isQuantifiable,
        IsActive: addonData.isActive,
        IsPublished: addonData.isPublished,
        SortOrder: parseInt(addonData.sortOrder) || 0,
        UpdatedDate: new Date().toISOString(),
      };

      const addonResponse = await axiosInstance.put(
        APIs.PUT_UPDATE_ADDON,
        addonPayload
      );

      if (addonResponse.data.status) {
        // Step 2: Add new prices (those with addonPriceID = 0)
        const newPrices = prices.filter((p) => p.addonPriceID === 0);
        let successCount = 0;
        let failCount = 0;

        for (const price of newPrices) {
          try {
            const pricePayload = {
              AddonID: addonData.addonID,
              PriceCurrency: price.currency.value,
              Price: parseFloat(price.price) || 0,
              ActualPrice: parseFloat(price.actualPrice) || 0,
              Duration: parseInt(price.duration) || 30,
              Country: price.country || "",
            };

            const priceResponse = await axiosInstance.post(
              APIs.POST_ADD_ADDON_PRICE,
              pricePayload
            );

            if (priceResponse.data.status) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            failCount++;
          }
        }

        if (newPrices.length > 0) {
          showSuccessToast(
            `Addon updated successfully${successCount > 0
              ? ` with ${successCount} new price(s)`
              : ""
            }${failCount > 0 ? `, ${failCount} price(s) failed` : ""}`
          );
        } else {
          showSuccessToast("Addon updated successfully");
        }

        navigate("/addons/manageAddons");
      } else {
        showErrorToast(addonResponse.data.message || "Failed to update addon");
      }
    } catch (error) {
      console.error(error);
      showErrorToast("Failed to update addon");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-2 mt-2">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/addons/manageAddons">Manage Addons</Link>
            </li>
            <li className="breadcrumb-item active">Update Addon</li>
          </ol>
          <h1 className="page-header mb-0">Update Addon</h1>
        </div>
        <div className="ms-auto">
          <button
            onClick={handleSubmit}
            className="btn btn-success btn-rounded px-4 rounded-pill"
            disabled={loading}
          >
            <i className="fa fa-save fa-lg me-2 ms-n2 text-success-900"></i>
            Update Addon
          </button>
        </div>
      </div>

      {/* Addon Details */}
      <Panel>
        <PanelHeader>Addon Details</PanelHeader>
        <PanelBody>
          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Addon Name"
                placeHolder="Enter Addon Name"
                type="text"
                value={addonData.addonName}
                onchange={(e) => handleInputChange(e, "addonName")}
              />
            </div>
            <div className="col-6">
              {/* Addon Type field hidden for now */}
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-12">
              <label>Description</label>
              <textarea
                className="form-control"
                placeholder="Enter addon description"
                value={addonData.addonDescription}
                onChange={(e) => handleInputChange(e, "addonDescription")}
                rows="3"
                style={{
                  borderRadius: "10px",
                  border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                  background: isDarkMode ? "#2d3436" : "#F9F9F9",
                  color: isDarkMode ? "#ffffff" : "#495057",
                }}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Value"
                placeHolder="e.g., 1 for 1 user, 10 for 10GB"
                type="number"
                value={addonData.addonValue}
                onchange={(e) => handleInputChange(e, "addonValue")}
                min="0"
              />
              <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Numeric value of the addon</small>
            </div>
            <div className="col-6">
              <label>Unit</label>
              <Select
                value={selectedUnit}
                onChange={(option) => {
                  setSelectedUnit(option);
                  setAddonData({
                    ...addonData,
                    addonUnit: option ? option.value : "",
                  });
                }}
                options={unitOptions}
                styles={isDarkMode ? DropDownStylesDark : DropDownStyles}
                placeholder="Select Unit"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuShouldBlockScroll={false}
                menuShouldScrollIntoView={false}
                closeMenuOnScroll={false}
              />
              <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Unit of measurement</small>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-6">
              <FormInput
                label="Sort Order"
                placeHolder="0"
                type="number"
                value={addonData.sortOrder}
                onchange={(e) => handleInputChange(e, "sortOrder")}
                min="0"
              />
              <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Lower numbers appear first</small>
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="isActive"
                  checked={addonData.isActive}
                  onChange={(e) => handleInputChange(e, "isActive")}
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
              </div>
            </div>
          </div>
        </PanelBody>
      </Panel>

      {/* Pricing Section */}
      <Panel>
        <PanelHeader>Pricing Management</PanelHeader>
        <PanelBody>
          <div className="alert alert-info">
            <i className="fa fa-info-circle me-2"></i>
            Manage pricing for different currencies and countries. Existing prices can be deleted, and new ones can be added.
          </div>

          {/* Existing Prices Table */}
          {prices.length > 0 && (
            <div className="mb-4">
              <h6 className="mb-3">Current Prices</h6>
              <div className="table-responsive">
                <table className="table table-bordered">
                  <thead
                    style={{
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Currency</th>
                      <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Price</th>
                      <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actual Price</th>
                      <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Duration</th>
                      <th style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Country</th>
                      <th className="text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Status</th>
                      <th className="text-center" style={{ backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa", color: isDarkMode ? "#ffffff" : "#000000", borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices.map((price) => {
                      const discount =
                        price.actualPrice > 0
                          ? (
                              ((price.actualPrice - price.price) /
                                price.actualPrice) *
                              100
                            ).toFixed(0)
                          : 0;

                      return (
                        <tr key={price.id}>
                          <td>
                            <strong>{price.currency.label}</strong>
                          </td>
                          <td>
                            {price.currency.value} {parseFloat(price.price).toFixed(2)}
                          </td>
                          <td>
                            {price.actualPrice > 0
                              ? `${price.currency.value} ${parseFloat(price.actualPrice).toFixed(2)}`
                              : "-"}
                          </td>
                          <td>
                            <span className="badge bg-info">
                              {price.duration} days
                            </span>
                          </td>
                          <td>{price.country || "-"}</td>
                          <td className="text-center">
                            {price.addonPriceID > 0 ? (
                              <span className="badge bg-primary">Saved</span>
                            ) : (
                              <span className="badge bg-warning">New</span>
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{
                                width: '32px',
                                height: '32px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px',
                                border: '1px solid #dc3545',
                                color: '#dc3545',
                                transition: 'all 0.2s ease',
                                padding: 0
                              }}
                              onClick={() => handleRemovePrice(price)}
                              title="Delete Price"
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#dc3545';
                                e.currentTarget.style.color = 'white';
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = '#dc3545';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                            >
                              <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Add New Price Form */}
          <div 
            className="card" 
            style={{ 
              backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
              border: isDarkMode ? "1px solid #404040" : "1px solid #dee2e6"
            }}
          >
            <div className="card-body">
              <h6 className="mb-3" style={{ color: isDarkMode ? "#ffffff" : "#000000" }}>Add New Price</h6>
              <div className="row g-3">
                <div className="col-md-3">
                  <label>Currency</label>
                  <CurrencySelector
                    value={newPrice.currency?.value}
                    onChange={(option) =>
                      setNewPrice({ ...newPrice, currency: option })
                    }
                    placeholder="Select Currency"
                    isClearable={true}
                  />
                </div>
                <div className="col-md-2">
                  <label>Price</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="5.00"
                    value={newPrice.price}
                    onChange={(e) => handlePriceInputChange(e, "price")}
                    step="0.01"
                    min="0"
                    style={{
                      borderRadius: "10px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#495057",
                      height: "44px",
                    }}
                  />
                </div>
                <div className="col-md-2">
                  <label>Actual Price</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="10.00"
                    value={newPrice.actualPrice}
                    onChange={(e) => handlePriceInputChange(e, "actualPrice")}
                    step="0.01"
                    min="0"
                    style={{
                      borderRadius: "10px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#495057",
                      height: "44px",
                    }}
                  />
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Before discount</small>
                </div>
                <div className="col-md-2">
                  <label>Duration (Days)</label>
                  <input
                    type="number"
                    className="form-control"
                    placeholder="30"
                    value={newPrice.duration}
                    onChange={(e) => handlePriceInputChange(e, "duration")}
                    min="1"
                    style={{
                      borderRadius: "10px",
                      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
                      background: isDarkMode ? "#2d3436" : "#F9F9F9",
                      color: isDarkMode ? "#ffffff" : "#495057",
                      height: "44px",
                    }}
                  />
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>30 = Monthly, 365 = Yearly</small>
                </div>
                <div className="col-md-3">
                  <label>Country (Optional)</label>
                  <CountrySelector
                    value={newPrice.country}
                    onChange={(countryCode) =>
                      setNewPrice({ ...newPrice, country: countryCode })
                    }
                    placeholder="Select Country"
                    isClearable={true}
                  />
                  <small style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>Select country for pricing</small>
                </div>
                <div className="col-md-2">
                  <label>&nbsp;</label>
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={handleAddPrice}
                  >
                    <i className="fa fa-plus me-1"></i> Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {prices.length === 0 && (
            <div className="alert alert-warning mt-3">
              <i className="fa fa-exclamation-triangle me-2"></i>
              No pricing added yet. Add at least one price for your addon.
            </div>
          )}
        </PanelBody>
      </Panel>

    </div>
  );
}

export default UpdateAddon;

